import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

interface KeyboardShortcut {
  key: string
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  description: string
  action: () => void
}

interface UseKeyboardShortcutsOptions {
  onShowShortcuts?: () => void
}

export function useKeyboardShortcuts({ onShowShortcuts }: UseKeyboardShortcutsOptions = {}) {
  const navigate = useNavigate()

  const shortcuts: KeyboardShortcut[] = [
    {
      key: 'n',
      metaKey: true,
      description: 'Create new model',
      action: () => navigate('/app/models/new'),
    },
    {
      key: 'p',
      metaKey: true,
      description: 'Go to predictions',
      action: () => navigate('/app/predictions'),
    },
    {
      key: 'd',
      metaKey: true,
      description: 'Go to data sources',
      action: () => navigate('/app/data-sources'),
    },
    {
      key: 'm',
      metaKey: true,
      description: 'Go to models',
      action: () => navigate('/app/models'),
    },
    {
      key: 'h',
      metaKey: true,
      description: 'Go to dashboard',
      action: () => navigate('/app/dashboard'),
    },
    {
      key: '/',
      metaKey: true,
      description: 'Show keyboard shortcuts',
      action: () => onShowShortcuts?.(),
    },
  ]

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      const shortcut = shortcuts.find((s) => {
        const keyMatch = s.key === event.key.toLowerCase()
        const metaMatch = s.metaKey ? event.metaKey || event.ctrlKey : true
        const ctrlMatch = s.ctrlKey ? event.ctrlKey : true
        const shiftMatch = s.shiftKey ? event.shiftKey : true

        return keyMatch && metaMatch && ctrlMatch && shiftMatch
      })

      if (shortcut) {
        event.preventDefault()
        shortcut.action()
      }
    },
    [navigate, onShowShortcuts]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return { shortcuts }
}

// Helper function to format keyboard shortcut for display
export function formatShortcut(shortcut: KeyboardShortcut): string {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const parts: string[] = []

  if (shortcut.metaKey) {
    parts.push(isMac ? '⌘' : 'Ctrl')
  }
  if (shortcut.ctrlKey) {
    parts.push('Ctrl')
  }
  if (shortcut.shiftKey) {
    parts.push('Shift')
  }
  parts.push(shortcut.key.toUpperCase())

  return parts.join('+')
}
